import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CheckCircle2, Sparkles } from "lucide-react";
import { PERSONALIZATION_TEMPLATES, type PersonalizationTemplate } from "@/lib/personalizationTemplates";

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (template: PersonalizationTemplate) => void;
}

export const TemplateDialog = ({ open, onOpenChange, onSelectTemplate }: TemplateDialogProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const roleTemplates = PERSONALIZATION_TEMPLATES.filter(t => t.category === 'role');
  const industryTemplates = PERSONALIZATION_TEMPLATES.filter(t => t.category === 'industry');

  const handleSelect = (template: PersonalizationTemplate) => {
    setSelectedTemplate(template.id);
    onSelectTemplate(template);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <DialogTitle>Template auswählen</DialogTitle>
          </div>
          <DialogDescription>
            Wähle ein vordefiniertes Template basierend auf deiner Rolle oder Branche. Alle Werte können danach angepasst werden.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="role" className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="role">Nach Rolle</TabsTrigger>
            <TabsTrigger value="industry">Nach Branche</TabsTrigger>
          </TabsList>

          <TabsContent value="role" className="mt-6 space-y-3">
            {roleTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleSelect(template)}
                className={cn(
                  "w-full flex items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.01] text-left",
                  "border-border hover:border-primary/50"
                )}
              >
                <div className="text-3xl flex-shrink-0">{template.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base text-foreground/90">
                    {template.name}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {template.description}
                  </p>
                </div>
              </button>
            ))}
          </TabsContent>

          <TabsContent value="industry" className="mt-6 space-y-3">
            {industryTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleSelect(template)}
                className={cn(
                  "w-full flex items-start gap-4 rounded-lg border-2 p-4 transition-all duration-200 hover:scale-[1.01] text-left",
                  "border-border hover:border-primary/50"
                )}
              >
                <div className="text-3xl flex-shrink-0">{template.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-base text-foreground/90">
                    {template.name}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {template.description}
                  </p>
                </div>
              </button>
            ))}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};